import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Upload, Send, Languages } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Switch } from "@/components/ui/switch";
import { AdminHeading, Card, Field, inputCls } from "@/components/admin/ui";

const SECTIONS = [
  { title: "Announcement bar", fields: [["announcement_enabled", "Show announcement bar", "bool"], ["announcement", "Announcement text"]] },
  { title: "Home hero", fields: [["hero_eyebrow", "Small label above title"], ["hero_title", "Main headline"], ["hero_subtitle", "Sub-headline", "area"], ["consumer_cta_label", "Citizen button label"], ["partner_cta_label", "Kabadiwala button label"]] },
  { title: "Website images", fields: [["image_hero", "Home hero image", "image"], ["image_home", "Citizens / sorting image", "image"], ["image_kabadiwala", "Kabadiwala image", "image"], ["image_about", "About page image", "image"]], enOnly: true },
  { title: "App links", fields: [["consumer_app_url", "Citizen app URL"], ["partner_app_url", "Kabadiwala partner app URL"], ["launch_city", "Launch city"]] },
  { title: "Scrap rates page", fields: [["rates_note", "Rates disclaimer", "area"]] },
  { title: "Kabadiwala page", fields: [["partner_title", "Headline"], ["partner_subtitle", "Sub-headline", "area"]] },
  { title: "About page", fields: [["about_title", "Headline"], ["about_body", "Intro paragraph", "area"]] },
  { title: "Contact details", fields: [["phone", "Phone"], ["whatsapp", "WhatsApp number (digits with country code)"], ["email", "Email"], ["support_email", "Support email"], ["address", "Office address", "area"], ["hours", "Working hours"]] },
  { title: "Enquiry email alerts (private)", fields: [["alert_enabled", "Email the team on every new enquiry", "bool"], ["alert_emails", "Alert recipients (comma separated, max 5)"]], enOnly: true, alerts: true },
  { title: "Social links (leave blank to hide)", fields: [["instagram", "Instagram URL"], ["linkedin", "LinkedIn URL"], ["facebook", "Facebook URL"], ["x", "X / Twitter URL"], ["youtube", "YouTube URL"]], enOnly: true },
];
const HI_KEYS = new Set(["announcement", "hero_eyebrow", "hero_title", "hero_subtitle", "consumer_cta_label", "partner_cta_label", "launch_city", "address", "hours", "rates_note", "about_title", "about_body", "partner_title", "partner_subtitle"]);

function ImageField({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image must be under 8 MB");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded — click Save changes to publish");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <div className="flex gap-3 items-center">
      <img src={value} alt="" className="w-28 h-20 rounded-xl object-cover border bg-[var(--paper-2)]" data-testid={`${testid}-preview`} />
      <div className="flex-1 space-y-2">
        <input type="file" accept="image/jpeg,image/png,image/webp" ref={ref} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-file`} />
        <button type="button" onClick={() => ref.current.click()} disabled={busy} className="btn-ghost !py-2 !px-3 text-xs" data-testid={`${testid}-upload-btn`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload new image
        </button>
        <div className="text-[11px] text-[var(--ink-soft)] truncate">{value}</div>
      </div>
    </div>
  );
}

export default function ContentEditor() {
  const { reload } = useSite();
  const [lang, setLang] = useState("en");
  const [en, setEn] = useState(null);
  const [hi, setHi] = useState({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/admin/content").then(({ data }) => { setEn(data.en); setHi(data.hi || {}); }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const f = lang === "en" ? en : hi;
  const setF = lang === "en" ? setEn : setHi;
  const save = async () => {
    setBusy(true);
    const { faqs, updated_at, lang: _l, ...rest } = f;
    const data = lang === "hi" ? Object.fromEntries(Object.entries(rest).filter(([k]) => HI_KEYS.has(k))) : rest;
    try {
      const { data: res } = await api.put("/admin/content", { data }, { params: { lang } });
      setEn(res.en); setHi(res.hi || {});
      await reload();
      toast.success(lang === "en" ? "Website updated · Hindi version will auto-update in a few seconds" : "Hindi version updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const testAlert = async () => {
    setTesting(true);
    try { await api.post("/admin/alerts/test"); toast.success("Test email sent"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  if (!en) return <Loader2 className="animate-spin text-[var(--leaf)]" />;
  const sections = SECTIONS.filter((s) => lang === "en" || !s.enOnly).map((s) => ({ ...s, fields: lang === "en" ? s.fields : s.fields.filter(([k]) => HI_KEYS.has(k)) })).filter((s) => s.fields.length);

  return (
    <div data-testid="admin-content">
      <AdminHeading title="Site content" subtitle="Edit text, images, links and contact details shown across the website.">
        <div className="flex items-center gap-2">
          <div className="inline-flex p-1 rounded-full bg-white border text-xs font-bold" data-testid="content-lang-switch">
            {[["en", "English"], ["hi", "हिंदी"]].map(([k, l]) => (
              <button key={k} onClick={() => setLang(k)} className={`px-3 py-1.5 rounded-full ${lang === k ? "bg-[var(--leaf)] text-white" : ""}`} data-testid={`content-lang-${k}`}>{l}</button>
            ))}
          </div>
          <button onClick={save} disabled={busy} className="btn-leaf !py-2.5 text-sm" data-testid="content-save-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes</button>
        </div>
      </AdminHeading>
      {lang === "hi" && (
        <div className="mb-5 flex gap-3 p-4 rounded-2xl bg-[var(--marigold-soft)] text-sm text-[#5C3A07]" data-testid="content-hi-note">
          <Languages size={18} className="shrink-0" /> Hindi text is auto-translated by AI whenever you save English. Correct anything here — your edits are kept until the English text changes again.
        </div>
      )}
      <div className="space-y-5">
        {sections.map((s) => (
          <Card key={s.title} className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="font-bold">{s.title}</h2>
              {s.alerts && <button onClick={testAlert} disabled={testing} className="btn-ghost !py-2 !px-3 text-xs" data-testid="alert-test-btn">{testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test email</button>}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {s.fields.map(([key, label, type]) => (
                <div key={key} className={type === "area" || type === "image" ? "md:col-span-2" : ""}>
                  {type === "bool" ? (
                    <label className="flex items-center gap-3 text-sm font-medium"><Switch checked={!!f[key]} onCheckedChange={(v) => setF((x) => ({ ...x, [key]: v }))} data-testid={`content-${key}`} /> {label}</label>
                  ) : (
                    <Field label={label} hint={lang === "hi" ? `English: ${en[key] || ""}` : undefined}>
                      {type === "image" ? <ImageField value={f[key]} onChange={(v) => setF((x) => ({ ...x, [key]: v }))} testid={`content-${key}`} />
                        : type === "area" ? <textarea rows={3} value={f[key] || ""} onChange={(e) => setF((x) => ({ ...x, [key]: e.target.value }))} className={inputCls} data-testid={`content-${key}`} />
                        : <input value={f[key] || ""} onChange={(e) => setF((x) => ({ ...x, [key]: e.target.value }))} className={inputCls} data-testid={`content-${key}`} />}
                    </Field>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
      <button onClick={save} disabled={busy} className="btn-leaf mt-6" data-testid="content-save-btn-bottom">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes</button>
    </div>
  );
}
